/**
 * لوحة الإرشاد داخل «قيّم أرض».
 *
 * تظهر تحت السؤال نفسه لا في طبقة عائمة: الإرشاد الذي يغطي الحقل الذي يتحدث
 * عنه إرشادٌ يعطّل ما يشرحه. وهي تقول شيئين في كل خطوة — **لماذا نسأل** ثم
 * **القيمة المقترحة بضغطة**.
 *
 * ونصّ «لماذا» ليس زينة: هو الفرق بين زائر ملأ استمارة وزائر فهم كيف يُبنى
 * الرقم. أهمها الخطوة الرابعة — أن السعر المطلوب لا يدخل الحساب.
 */

import { color } from '@/design/tokens';
import { JOURNEY_EXAMPLE } from './journey';

interface StepGuide {
  why: string;
  /** نصّ زر التعبئة — غيابه يعني خطوة بلا قيمة تُملأ. */
  fill?: string;
  hint?: string;
}

const STEP_GUIDE: StepGuide[] = [
  {
    why: 'نبدأ بالفئة لأنها تحدد عالم المقارنة كاملاً — الأرض لا تُقارن بمبنى أبداً.',
    fill: 'اختر «أرض»',
  },
  {
    why: 'اخترنا لك حياً فيه صفقات مسجّلة كافية، كي ترى نتيجة كاملة في جولتك الأولى.',
    fill: `استخدم «${JOURNEY_EXAMPLE.district}»`,
  },
  {
    why: 'النطاق السعري سعرُ متر — والمساحة هي التي تحوّله إلى مبلغ.',
    fill: `استخدم ${JOURNEY_EXAMPLE.area} م²`,
  },
  {
    why: 'هذا الرقم لا يدخل في حساب النطاق إطلاقاً — نقارنه به فقط. لو دخل لصار المنتج يبرّر السعر بدل أن يحكم عليه.',
    fill: 'استخدم ٣,٠٠٠,٠٠٠ ر.س',
  },
  {
    why: 'الموقع اختياري: يرفع الدقة ولا يمنع النتيجة. اضغط «تخطَّ» ونشوف ما وصلنا إليه.',
    hint: 'الخطوة الأخيرة — بعدها النتيجة.',
  },
];

export function JourneyHint({
  step,
  total,
  onFill,
  onExit,
}: {
  step: number;
  total: number;
  /** يملأ قيمة الخطوة الحالية — غيابه يخفي الزر. */
  onFill?: () => void;
  onExit: () => void;
}): JSX.Element | null {
  const guide = STEP_GUIDE[step];
  if (!guide) return null;

  return (
    <aside
      data-testid="journey-hint"
      style={{
        marginTop: 20,
        padding: '14px 16px',
        borderRadius: 12,
        background: color.tealFaint,
        border: `1px solid ${color.tealBorder}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <span className="nq-scheme">NITAQ / TOUR</span>
        <span className="nq-num nq-micro" dir="ltr">
          {step + 1} / {total}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="nq-btn nq-btn--quiet nq-btn--sm" onClick={onExit}>
          إنهاء الجولة
        </button>
      </div>

      <p style={{ fontSize: 13, lineHeight: 1.9, color: color.textBody }}>{guide.why}</p>

      {guide.fill && onFill ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" className="nq-btn nq-btn--ghost nq-btn--sm" onClick={onFill}>
            {guide.fill}
          </button>
          <span
            style={{ marginInlineStart: 10, fontSize: 11.5, color: color.textSubtle }}
          >
            أو اكتب قيمتك أنت.
          </span>
        </div>
      ) : null}

      {guide.hint ? (
        <p style={{ marginTop: 10, fontSize: 11.5, color: color.textSubtle }}>{guide.hint}</p>
      ) : null}
    </aside>
  );
}
